import React, { useState, useEffect } from 'react';
import DashboardLayout from './layout/DashboardLayout';
import AdminDashboard from '../pages/admin/Dashboard';
import { getCurrentUser } from '../services/userService';

// User Current Tasks Component for regular users (extracted from original Home.tsx)
import { Link, useNavigate } from 'react-router-dom';
import API_CONFIG from '../config/api';
import socketService from '../services/socketService';
import { toast } from 'react-toastify';

interface TestRequest {
  _id: string;
  displayId?: number; // Display ID for tracking (شناسه)
  patientInfo: {
    firstName: string;
    lastName: string;
    patientType: string;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string;
  instructions?: string;
  editDeadline?: string;
  editVersion?: number;
}

// Function to translate status to Persian
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    incomplete: 'ناتمام',
    reviewing: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    edited: 'ویرایش شده',
    expired: 'منقضی شده'
  };
  
  // Clean the status and check for exact match
  const cleanStatus = status?.trim().toLowerCase();
  const translation = statusMap[cleanStatus];
  
  // Debug log if translation is missing
  if (!translation && cleanStatus) {

  }
  
  return translation || status;
};

// Function to get status color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    incomplete: 'bg-yellow-100 text-yellow-800',
    reviewing: 'bg-orange-100 text-orange-800',
    approved: 'bg-green-100 text-green-800', 
    rejected: 'bg-red-100 text-red-800',
    edited: 'bg-blue-100 text-blue-800',
    expired: 'bg-gray-100 text-gray-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

// Function to translate patient type
const translatePatientType = (type: string): string => {
  const typeMap: Record<string, string> = {
    self: 'خودم',
    child: 'فرزند',
    spouse: 'همسر',
    father: 'پدر',
    mother: 'مادر',
    other: 'سایر'
  };
  return typeMap[type] || type;
};

// Function to calculate remaining edit time for rejected requests
const calculateRemainingEditTime = (editDeadline: string | undefined, status: string): string => {
  if (status !== 'rejected' || !editDeadline) return '';
  
  const deadline = new Date(editDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  
  if (diffMs <= 0) return 'منقضی شده';
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 0) {
    return `${diffHours} ساعت و ${diffMinutes} دقیقه`;
  } else {
    return `${diffMinutes} دقیقه`;
  }
};

// Function to check if user can edit a rejected request
const canEditRequest = (editDeadline: string | undefined, status: string): boolean => {
  if (status !== 'rejected' || !editDeadline) return false;
  
  const deadline = new Date(editDeadline);
  const now = new Date();
  return now.getTime() <= deadline.getTime();
};

// Dynamic Links Component
const DynamicLinks: React.FC = () => {
  const [links, setLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_CONFIG.getUrl('links/public/position/dashboard'));
      
      if (!response.ok) {
        throw new Error('خطا در دریافت لینک‌ها');
      }
      
      const data = await response.json();
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت لینک‌ها');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-500 hover:bg-blue-600 text-white',
      green: 'bg-green-500 hover:bg-green-600 text-white',
      purple: 'bg-purple-500 hover:bg-purple-600 text-white',
      red: 'bg-red-500 hover:bg-red-600 text-white',
      yellow: 'bg-yellow-500 hover:bg-yellow-600 text-black',
      indigo: 'bg-indigo-500 hover:bg-indigo-600 text-white',
      pink: 'bg-pink-500 hover:bg-pink-600 text-white',
      gray: 'bg-gray-500 hover:bg-gray-600 text-white'
    };
    return colorMap[color] || 'bg-blue-500 hover:bg-blue-600 text-white';
  };

  const getIcon = (iconName: string) => {
    const iconMap: Record<string, string> = {
      link: '🔗',
      home: '🏠',
      info: 'ℹ️',
      help: '❓',
      contact: '📞',
      about: '👥',
      services: '⚙️',
      news: '📰',
      support: '🆘',
      docs: '📚'
    };
    return iconMap[iconName] || '🔗';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">لینک‌های مفید</h2>
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">لینک‌های مفید</h2>
        <div className="text-center text-red-500 py-4">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (links.length === 0) {
    return null; // Don't show the component if no links
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <h2 className="text-xl font-bold text-slate-800 mb-4">لینک‌های مفید</h2>
      <div className="flex flex-wrap gap-3">
        {links.map((link) => (
          <a
            key={link._id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg ${getColorClasses(link.color)}`}
            title={link.description || link.title}
          >
            <span className="text-lg">{getIcon(link.icon)}</span>
            <span>{link.title}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

// User Current Tasks Component
const UserCurrentTasks: React.FC = () => {
  const navigate = useNavigate();
  const [currentTasks, setCurrentTasks] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchCurrentTasks();
  }, []); // Empty dependency array ensures this runs only once

  // Setup socket listener for test request updates
  useEffect(() => {
    const handleSocketUpdate = (notification: any) => {

      // Refresh current tasks for any socket notification
      // This covers new requests, status updates, messages, etc.
      fetchCurrentTasks();
    };

    // Set up socket listener - this will catch all test request related updates
    socketService.setOnNewTestRequest(handleSocketUpdate);

    // Also set up a periodic refresh to catch any missed updates
    const refreshInterval = setInterval(() => {

      fetchCurrentTasks();
    }, 30000); // Refresh every 30 seconds

    // Cleanup
    return () => {
      socketService.setOnNewTestRequest(() => {});
      clearInterval(refreshInterval);
    };
  }, []);

  const fetchCurrentTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        setLoading(false);
        return;
      }
      
      // Fetch all requests and filter by current task statuses
      const response = await fetch(API_CONFIG.getUrl('test-requests?limit=50'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('خطا در دریافت اطلاعات');
      }
      
      const data = await response.json();
      
      // Filter requests with current task statuses
      const currentTaskStatuses = ['reviewing', 'approved', 'rejected', 'edited', 'incomplete'];
      const filteredRequests = data.data.filter((request: TestRequest) => 
        currentTaskStatuses.includes(request.status)
      );
      
      // Deduplicate by _id
      const uniqueMap: Record<string, TestRequest> = {};
      filteredRequests.forEach((req: TestRequest) => {
        const idStr = req._id.toString();
        if (!uniqueMap[idStr]) {
          uniqueMap[idStr] = req;
        }
      });
      const uniqueRequests = Object.values(uniqueMap);
      
      // Sort by most recent first
      uniqueRequests.sort((a: TestRequest, b: TestRequest) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Limit to 10 most recent
      const finalRequests = uniqueRequests.slice(0, 10);
      
      setCurrentTasks(finalRequests);
    } catch (err) {
      setError('خطا در دریافت کارهای جاری');

    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">کارهای جاری</h2>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">کارهای جاری</h2>
        <div className="text-center text-red-500 py-8">
          <p>{error}</p>
          <button 
            onClick={fetchCurrentTasks} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-800">آزمایش های جاری</h2>
        <Link 
          to="/my-tests" 
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          آزمایش های من →
        </Link>
      </div>
      
      {currentTasks.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
            <Link 
            to="/new-test-request" 
            className="mt-4 inline-block px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            onClick={() => toast.warning('شما در حال ثبت درخواست جدید هستید')}
          >
             درخواست آزمایش جدید
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">کد پیگیری</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">بیمار</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">وضعیت</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">زمان باقی‌مانده</th>
                <th className="text-right py-3 px-2 font-semibold text-sm text-gray-700">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {currentTasks.map((request, index) => (
                <tr key={`${request._id}-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 text-sm font-medium text-gray-900">
                    {request.displayId || request._id.slice(-8)}
                  </td>
                  <td className="py-3 px-2 text-sm">
                    <div>
                      <div className="font-medium text-gray-900">
                        {request.patientInfo.firstName} {request.patientInfo.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {translatePatientType(request.patientInfo.patientType)}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                        {translateStatus(request.status)}
                      </span>
                      {request.editVersion && request.editVersion > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title={`ویرایش ${request.editVersion}`}>
                          {request.editVersion}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm">
                    {request.status === 'rejected' && request.editDeadline ? (
                      <div className="text-xs">
                        {canEditRequest(request.editDeadline, request.status) ? (
                          <span className="text-blue-600 font-medium">
                            ✏️ {calculateRemainingEditTime(request.editDeadline, request.status)}
                          </span>
                        ) : (
                          <span className="text-red-600">❌ منقضی شده</span>
                        )}
                      </div>
                    ) : request.status === 'approved' ? (
                      <span className="text-green-600 text-xs">💰 آماده پرداخت</span>
                    ) : (
                      <span className="text-gray-500 text-xs">---</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Link 
                        to={`/test-requests/${request._id}`} 
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-200 transition-colors"
                      >
                        مشاهده
                      </Link>
                      {request.status === 'rejected' && canEditRequest(request.editDeadline, request.status) && (
                        <Link 
                          to={`/edit-request/${request._id}`} 
                          className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-md hover:bg-green-200 transition-colors"
                        >
                          ویرایش
                        </Link>
                      )}
                      {request.status?.trim().toLowerCase() === 'incomplete' && (
                        <Link 
                          to={`/edit-request/${request._id}`} 
                          className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-md hover:bg-orange-200 transition-colors"
                        >
                          ادامه
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Regular User Home Component
const RegularUserHome: React.FC = () => {
  return (
    <DashboardLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold text-slate-800 mb-6">داشبورد آزمایشگاه رازی</h1>
        
        {/* Welcome Card */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 mb-6">
          <p className="text-slate-600"> <img src={'/assets/img/logoRazi.png'} alt="logo" className="w-10 h-10" /> آزمایشگاه رازی</p>
        </div>

        {/* Dynamic Links */}
        <DynamicLinks />

        {/* User Current Tasks Table */}
        <UserCurrentTasks />
      </div>
    </DashboardLayout>
  );
};

// Main Dashboard Router Component
const DashboardRouter: React.FC = () => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      setLoading(true);
      
      // First try to get user data from localStorage
      const userDataFromStorage = localStorage.getItem('userData');
      if (userDataFromStorage) {
        try {
          const userData = JSON.parse(userDataFromStorage);
          if (userData.userType) {
            setUserRole(userData.userType);
            setLoading(false);
            return;
          }
        } catch (error) {

        }
      }

      // If localStorage doesn't have userType, fetch from API
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        setLoading(false);
        return;
      }

      const response = await getCurrentUser();
      if (response.success && response.data) {
        const userData = response.data;
        setUserRole(userData.userType || 'User');
        
        // Update localStorage with complete user data including userType
        localStorage.setItem('userData', JSON.stringify(userData));
      } else {
        setError('خطا در دریافت اطلاعات کاربر');
      }
    } catch (err) {

      setError('خطا در بررسی نقش کاربر');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-center text-red-500">
            <p>{error}</p>
            <button 
              onClick={checkUserRole} 
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              تلاش مجدد
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Render based on user role
  if (userRole === 'Admin') {
    return <DashboardLayout><AdminDashboard /></DashboardLayout>;
  } else {
    return <RegularUserHome />;
  }
};

export default DashboardRouter;